// ============================================================================
// ROLE PERMISSIONS SCREEN - Digital Approval System
// ============================================================================
// Admin-only screen to manage Keycloak role → permission mappings.
// One row per role. Layout mirrors the enterprise master-screen pattern
// (module-master-container + ModuleHeader + data-table + search-panel).
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  useRolePermissions,
  useCreateRolePermission,
  useUpdateRolePermission,
  useDeleteRolePermission,
} from 'hooks/api/admin/useRolePermissions';
import type {
  RolePermission,
  RolePermissionFilterParams,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
} from 'api/types/admin/rolePermissions.types';
import RolePermissionModal, { type RolePermissionModalMode } from 'components/modals/admin/RolePermissionModal';
import { SUPER_ROLE_CODE, hasAction } from 'config/rolePermissions';
import { useAuth } from 'hooks/api';

interface RolePermissionsScreenProps {
  className?: string;
}

// ============================================================================
// ROW ACTION MENU
// ============================================================================

interface RowActionMenuProps {
  row: RolePermission;
  canEdit: boolean;
  canDelete: boolean;
  onView: (row: RolePermission) => void;
  onEdit: (row: RolePermission) => void;
  onDelete: (row: RolePermission) => void;
  onMenuStateChange: (rowId: string, isOpen: boolean) => void;
}

const RowActionMenu: React.FC<RowActionMenuProps> = ({ row, canEdit, canDelete, onView, onEdit, onDelete, onMenuStateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // The super-role (das_system) is fully immutable — only "View" is offered.
  // Edit + Delete are hidden so admins can't tamper with its row, even
  // through the UI. Permissions for das_system are always derived from
  // the frontend catalog at runtime (see useAuth.ts override).
  const isSuperRow = row.roleCode === SUPER_ROLE_CODE;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    onMenuStateChange(row.id as string, isOpen);
  }, [isOpen, row.id, onMenuStateChange]);

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Row actions"
      >
        <IconSystem.moreVertical size={16} />
      </button>

      {isOpen && (
        <div className="action-menu-dropdown">
          <button className="action-menu-item" onClick={() => { onView(row); setIsOpen(false); }}>
            <IconSystem.eye size={18} />
            <span>View Details</span>
          </button>
          {!isSuperRow && canEdit && (
            <button className="action-menu-item" onClick={() => { onEdit(row); setIsOpen(false); }}>
              <IconSystem.edit size={18} />
              <span>Edit</span>
            </button>
          )}
          {!row.isSystemRole && canDelete && (
            <>
              <div className="action-menu-divider" />
              <button
                className="action-menu-item action-menu-item-danger"
                onClick={() => { onDelete(row); setIsOpen(false); }}
              >
                <IconSystem.trash size={18} />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RolePermissionsScreen: React.FC<RolePermissionsScreenProps> = ({ className = '' }) => {
  // ---- UI state ----
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeMenuRowId, setActiveMenuRowId] = useState<string | null>(null);

  // ---- Filter state ----
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [typeFilter, setTypeFilter] = useState<'all' | 'system' | 'custom'>('all');

  // ---- Server-side query state ----
  const [queryParams, setQueryParams] = useState<RolePermissionFilterParams>({
    page: 1,
    pageSize: 10,
    searchTerm: '',
    activeOnly: true,
  });

  const { items, pagination, loading, error, refetch } = useRolePermissions(queryParams);

  // Configuration-driven action gates for the Role Permissions module.
  // The `add`, `edit`, `delete` actions on `role-permissions-management`
  // control whether the corresponding controls render. `view` is implicit
  // — module visibility (sidebar entry) IS the view permission.
  const { user: authUser } = useAuth();
  const canAdd = useMemo(
    () => hasAction('role-permissions-management', 'add', authUser?.rolePermissions),
    [authUser]
  );
  const canEdit = useMemo(
    () => hasAction('role-permissions-management', 'edit', authUser?.rolePermissions),
    [authUser]
  );
  const canDelete = useMemo(
    () => hasAction('role-permissions-management', 'delete', authUser?.rolePermissions),
    [authUser]
  );
  const { create } = useCreateRolePermission();
  const { update } = useUpdateRolePermission();
  const { remove } = useDeleteRolePermission();

  // ---- Modal state ----
  const [modalMode, setModalMode] = useState<RolePermissionModalMode | null>(null);
  const [editing, setEditing] = useState<RolePermission | null>(null);

  // ---- Notification state ----
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // ---- Delete confirmation state ----
  const [pendingDelete, setPendingDelete] = useState<RolePermission | null>(null);

  // ---------------------------------------------------------------------------
  // DEBOUNCED SEARCH
  // ---------------------------------------------------------------------------
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: value, page: 1 }));
    }, 400);
  }, []);

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  // ---------------------------------------------------------------------------
  // CLIENT-SIDE TYPE FILTER (system/custom is not a server param)
  // ---------------------------------------------------------------------------
  const filteredItems = useMemo(() => {
    if (typeFilter === 'all') return items;
    return items.filter((r) =>
      typeFilter === 'system' ? r.isSystemRole : !r.isSystemRole
    );
  }, [items, typeFilter]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleMenuStateChange = useCallback((rowId: string, isOpen: boolean) => {
    setActiveMenuRowId(isOpen ? rowId : null);
  }, []);

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    if (type === 'success') {
      setTimeout(() => setNotification(null), 4000);
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditing(null);
  }, []);

  const handleCreate = useCallback(() => {
    setEditing(null);
    setModalMode('create');
  }, []);

  const handleView = useCallback((row: RolePermission) => {
    setEditing(row);
    setModalMode('view');
  }, []);

  const handleEdit = useCallback((row: RolePermission) => {
    setEditing(row);
    setModalMode('edit');
  }, []);

  const handleDeleteRequest = useCallback((row: RolePermission) => {
    setPendingDelete(row);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await remove(pendingDelete.id);
      showNotification('success', `Role "${pendingDelete.roleName}" deleted.`);
      refetch();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.message ?? err?.message ?? 'Delete failed.');
    } finally {
      setPendingDelete(null);
    }
  }, [pendingDelete, remove, refetch, showNotification]);

  const handleSubmit = useCallback(
    async (payload: CreateRolePermissionRequest | UpdateRolePermissionRequest) => {
      try {
        if (modalMode === 'create') {
          await create(payload as CreateRolePermissionRequest);
          showNotification('success', 'Role permission created successfully.');
        } else if (modalMode === 'edit' && editing) {
          await update(editing.id, payload as UpdateRolePermissionRequest);
          showNotification('success', 'Role permission updated successfully.');
        }
        closeModal();
        refetch();
      } catch (err: any) {
        showNotification('error', err?.response?.data?.message ?? err?.message ?? 'Save failed.');
        throw err; // keep modal open
      }
    },
    [modalMode, editing, create, update, refetch, closeModal, showNotification]
  );

  const handlePageChange = useCallback((page: number) => {
    setQueryParams((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      activeOnly: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('active');
    setTypeFilter('all');
    setQueryParams((prev) => ({ ...prev, activeOnly: true, page: 1 }));
  }, []);

  // ---------------------------------------------------------------------------
  // MODULE HEADER CONFIG
  // ---------------------------------------------------------------------------
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add New Role',
        onClick: handleCreate,
        tooltip: 'Add New Role Permission',
        variant: 'icon',
        // Gated by the `add` action on role-permissions-management. If the
        // user's role lacks it, the button is hidden entirely (rather than
        // shown-but-disabled) — same convention as the row Edit/Delete.
        visible: canAdd,
      },
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} className={loading ? 'spinner' : ''} />,
        label: 'Refresh',
        onClick: () => refetch(),
        tooltip: 'Refresh',
        variant: 'icon',
        disabled: loading,
        visible: true,
      },
    ],
    [handleCreate, refetch, loading, canAdd]
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  const colSpan = 7;

  return (
    <div className={`module-master-container ${className}`}>
      <ModuleHeader
        visible={true}
        title="Role Permissions"
        description="Map IAM roles to the modules and actions they can see and do."
        searchConfig={{
          value: searchQuery,
          onChange: handleSearch,
          placeholder: 'Search by role code or name...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: setViewMode,
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      {/* Centered toast — rendered via portal to escape header/content layout */}
      {notification && createPortal(
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10080,
            minWidth: '280px',
            maxWidth: '480px',
            padding: '14px 20px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            backgroundColor: notification.type === 'success' ? '#2563eb' : '#dc2626',
            color: '#ffffff',
            boxShadow: '0 12px 24px -8px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.15)',
            fontSize: '0.9375rem',
            fontWeight: 500,
          }}
          role="status"
          aria-live="polite"
        >
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            style={{
              background: 'rgba(255, 255, 255, 0.18)',
              border: 'none',
              borderRadius: '4px',
              fontSize: '18px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '2px 8px',
              color: '#ffffff',
              flexShrink: 0,
            }}
            onClick={() => setNotification(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>,
        document.body
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '6px',
            margin: '0 1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
          }}
        >
          Failed to load role permissions: {error.message}
          <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>
            Retry
          </button>
        </div>
      )}

      <div className="module-content" style={{ marginTop: '12px' }}>
        <div className="data-grid-container">
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Role Code</th>
                  <th>Role Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Modules</th>
                  <th>Status</th>
                  <th className="action-column" style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Show the "Loading…" placeholder ONLY on the very first
                    fetch (when we have no rows yet). For subsequent refetches
                    triggered by save/update/delete or filter changes we keep
                    the existing rows visible — the refresh button shows a
                    spinner so the user knows a fetch is in flight without
                    the grid blanking out. */}
                {loading && filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                      Loading role permissions…
                    </td>
                  </tr>
                ) : !loading && filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                      {searchQuery ? `No roles match "${searchQuery}".` : 'No role permissions configured.'}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((row) => (
                    <tr
                      key={row.id as string}
                      className={activeMenuRowId === row.id ? 'has-active-menu' : ''}
                    >
                      <td>
                        <code
                          style={{
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                          }}
                        >
                          {row.roleCode}
                        </code>
                      </td>
                      <td>{row.roleName}</td>
                      <td style={{ color: 'var(--text-secondary, #6b7280)' }}>
                        {row.description ?? '—'}
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: row.isSystemRole ? '#eef2ff' : '#f3f4f6',
                            color: row.isSystemRole ? '#4338ca' : '#6b7280',
                            border: `1px solid ${row.isSystemRole ? '#c7d2fe' : '#d1d5db'}`,
                          }}
                        >
                          {row.isSystemRole ? 'System' : 'Custom'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {row.visibleModules?.length ?? 0}
                      </td>
                      <td>
                        <span className={`badge badge-${row.isActive ? 'active' : 'inactive'}`}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="action-column">
                        <RowActionMenu
                          row={row}
                          canEdit={canEdit}
                          canDelete={canDelete}
                          onView={handleView}
                          onEdit={handleEdit}
                          onDelete={handleDeleteRequest}
                          onMenuStateChange={handleMenuStateChange}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalItems > 0 && (
            <div className="pagination-footer">
              <div className="pagination-info">
                <span>
                  Showing{' '}
                  {pagination.totalItems === 0
                    ? 0
                    : (pagination.currentPage - 1) * pagination.pageSize + 1}
                  –{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of{' '}
                  {pagination.totalItems} roles
                </span>
                <select
                  value={queryParams.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    marginLeft: '1rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="pagination-controls">
                <button
                  className="page-btn"
                  disabled={!pagination.hasPreviousPage}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  <IconSystem.chevronLeft size={16} />
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === pagination.totalPages ||
                      Math.abs(page - pagination.currentPage) <= 1
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>…</span>
                        )}
                        <button
                          className={`page-btn ${pagination.currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  className="page-btn"
                  disabled={!pagination.hasNextPage}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  <IconSystem.chevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Filter panel (sibling) */}
        {filterOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '320px',
              background: 'var(--bg-primary, #fff)',
              borderLeft: '1px solid var(--border-color, #e5e7eb)',
              boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem 1.25rem',
                borderBottom: '1px solid var(--border-color, #e5e7eb)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Filters</h3>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-muted, #6b7280)',
                }}
                aria-label="Close filters"
              >
                <IconSystem.x size={18} />
              </button>
            </div>

            <div style={{ flex: 1, padding: '1.25rem', overflowY: 'auto' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--text-secondary, #374151)',
                  }}
                >
                  Status
                </label>
                {(['all', 'active', 'inactive'] as const).map((opt) => (
                  <label
                    key={opt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    <input
                      type="radio"
                      name="status-filter"
                      checked={statusFilter === opt}
                      onChange={() => setStatusFilter(opt)}
                    />
                    {opt === 'all' ? 'All' : opt === 'active' ? 'Active' : 'Inactive'}
                  </label>
                ))}
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--text-secondary, #374151)',
                  }}
                >
                  Role Type
                </label>
                {(['all', 'system', 'custom'] as const).map((opt) => (
                  <label
                    key={opt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    <input
                      type="radio"
                      name="type-filter"
                      checked={typeFilter === opt}
                      onChange={() => setTypeFilter(opt)}
                    />
                    {opt === 'all' ? 'All Types' : opt === 'system' ? 'System Roles' : 'Custom Roles'}
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: '0.875rem 1.25rem',
                borderTop: '1px solid var(--border-color, #e5e7eb)',
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'flex-end',
              }}
            >
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={handleApplyFilters}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {pendingDelete && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10060,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPendingDelete(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              maxWidth: '440px',
              width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 10061,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconSystem.trash size={20} color="#dc2626" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '0 0 0.25rem 0', color: '#111827' }}>
                  Delete Role Permission
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                  Are you sure you want to delete role <strong>{pendingDelete.roleName}</strong> ({pendingDelete.roleCode})? This action cannot be undone.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPendingDelete(null)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Form/View Modal */}
      {modalMode && (
        <RolePermissionModal
          mode={modalMode}
          initial={editing}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default RolePermissionsScreen;
